//@ts-check
import { mustBeHtmlElement } from "common/dom-utils";
import { PurchasableComponent } from "./PurchasableComponent";

/**
 * Represents the whole section containing licensing + adding fonts to the cart
 * at the end of a typeface page.
 *
 * Children:
 * - LicensingSelector
 * - FontsSelector
 */
export class PurchasingSectionComponent {
  /** @type {PurchasingSectionComponent | null} */
  static _instance = null;

  /**
   * Get the instance on this page (there can only be one).
   * @returns {PurchasingSectionComponent}
   */
  static getInstance() {
    if (!this._instance) {
      this._instance = new PurchasingSectionComponent(
        mustBeHtmlElement(
          document.querySelector(".purchase-section"),
          "purchase section"
        )
      );
    }
    return this._instance;
  }

  constructor(sectionElem) {
    this.element = mustBeHtmlElement(sectionElem, "sectionElem");
  }

  get fontFamilyId() {
    return this.element.dataset.fontFamilyId;
  }

  updateFromPayload(payload) {
    const cart = payload?.cart;
    if (!cart) {
      console.error("No cart data in response:", payload);
      return;
    }
    this.updateShoppingCartTotal(cart.total_amount_formatted);
    this.updateButtonsAndHighlights(cart, payload.prices); // TODO: rename prices to purchasableInfos
    this.updatePrices(payload.prices);
  }

  updateShoppingCartTotal(totalAmountFormatted) {
    if (totalAmountFormatted === undefined || totalAmountFormatted === null) {
      console.error("Total amount is undefined or null");
      return;
    }

    const totalElement = mustBeHtmlElement(
      document.querySelector(".cart__amount")
    );

    totalElement.innerHTML = totalAmountFormatted;
  }
  updateButtonsAndHighlights(cart, purchasableInfos) {
    const items = cart.items || [];
    const included = cart.automatically_included_purchasables || {};

    const purchasableInfosMap = new Map();
    purchasableInfos.forEach((info) => {
      return purchasableInfosMap.set(info.purchasable.tid, info);
    });

    const purchasableRefsInCart = new Set(
      items
        .filter((item) => item.purchasable)
        .map((item) => `${item.purchasable.type}_${item.purchasable.id}`)
    );
    const purchasableRefsIncluded = new Set(
      (included || []).map((p) => `${p.type}_${p.id}`)
    );

    console.log("Items in cart:", purchasableRefsInCart);
    console.log("Included purchasables:", purchasableRefsIncluded);

    const purchasableElems = document.querySelectorAll(`.buyable-item`);

    const findShoppingCartItemByPurchasableRef = (purchasableRef) => {
      const [type, id] = purchasableRef.split("_");

      // Find the corresponding cart item, so that we can pass its ID to the backend
      const item = items.find(
        (i) =>
          i.purchasable &&
          i.purchasable.id.toString() === id &&
          i.purchasable.type === type
      );
      if (!item) {
        throw new Error(
          `Could not find cart item for purchasableRef: ${purchasableRef}`
        );
      }
      return item;
    };

    // Iterate over all the purchasable rows and update the button/highlight
    purchasableElems.forEach((item) => {
      const purchasableElem = mustBeHtmlElement(
        item,
        "purchasableElem in .buyable-item"
      );

      const iterationPurchasableRef = `${purchasableElem.dataset.purchasableType}_${purchasableElem.dataset.purchasableId}`;
      const info = purchasableInfosMap.get(iterationPurchasableRef);

      const isAdded = info.cart_status.is_added;
      const isAutoIncluded = info.cart_status.is_auto_included;
      const includedReason = info.cart_status.included_reason;

      const purchasable = new PurchasableComponent(purchasableElem);

      // The conditional logic should be match the one in `Store::ApplicationHelper#cart_link`
      if (isAdded) {
        // --- (a) --- The item has been explicitly added to the cart by the user.
        const item = findShoppingCartItemByPurchasableRef(
          iterationPurchasableRef
        );
        purchasable.makeRemovable(item);
      } else if (isAutoIncluded) {
        // --- (b) --- The item has been automatically included in the cart.
        if (purchasable.isFamily) {
          // The family has been automically included because the total price of its added children reached the family/package price.
          // In this case we want the user to be able to remove it from the cart.
          purchasable.makeRemovable(null);
        } else if (purchasable.isPackage) {
          if (includedReason === "package_price_reached") {
            // The package has been automically included because the total price of its added children reached the package price.
            // In this case we want the user to be able to remove it from the cart.
            purchasable.makeRemovable(null);
          } else {
            // The package is included because the family has been added or because the package itself has been added.
            // In this case it's not removable.
            purchasable.makeIncluded();
          }
        } else {
          purchasable.makeIncluded();
        }
      } else {
        // --- (c) --- The item is not in the cart.
        purchasable.makeAddable();
      }
    });
  }

  // Handles payload generated by Checkout::FontFamilyLicensedPricesSerializer on the server.
  updatePrices(pricesPayload) {
    if (!pricesPayload) return;

    pricesPayload.forEach((entryPayload) => {
      const element = document.querySelector(
        `.buyable-item[data-purchasable-tid="${entryPayload.purchasable.tid}"]`
      );
      if (!element) return;

      const buyableElement = mustBeHtmlElement(
        element,
        `buyable item for ${entryPayload.purchasable.tid}`
      );
      const purchasable = new PurchasableComponent(buyableElement);
      purchasable.setPrice(entryPayload);
    });
  }
}
